import { useMemo } from 'react'
import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuSeparator
} from '@/components/ui/context-menu'
import { useTranslation } from '@/translations/i18n'
import type { Subgeo } from '../types'
import { NeighborhoodActions } from './neighborhood-actions'

interface NeighborhoodContextMenuProps {
	contextMenuState: {
		x: number
		y: number
		neighborhoodId: string
		neighborhoodName: string
	} | null
	subgeos: Subgeo[]
	onClose: () => void
}

export const NeighborhoodContextMenu = ({
	contextMenuState,
	subgeos,
	onClose
}: NeighborhoodContextMenuProps) => {
	const { t } = useTranslation()

	const currentSubgeo = contextMenuState
		? subgeos.find((s) =>
				s.neighborhoodIds.includes(contextMenuState.neighborhoodId)
			)
		: null

	// Leaflet handles the right click on the polygon itself and hands us the
	// cursor point, so there is no DOM trigger for the menu to sit against.
	const { x = 0, y = 0 } = contextMenuState ?? {}
	const anchor = useMemo(
		() => ({
			getBoundingClientRect: () =>
				DOMRect.fromRect({ height: 0, width: 0, x, y })
		}),
		[x, y]
	)

	if (!contextMenuState) {
		return null
	}

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			onClose()
		}
	}

	return (
		<ContextMenu onOpenChange={handleOpenChange} open>
			<ContextMenuContent
				anchor={anchor}
				className='flex w-[280px] flex-col'
			>
				<ContextMenuItem>
					<div className='flex w-[180px] flex-col items-start gap-0.5 [&_button]:min-h-[22px] [&_button]:p-0 [&_button]:text-danger-600 [&_button]:text-xs [&_button]:hover:text-danger-700'>
						<h5 className='w-full truncate font-semibold text-neutral-900 text-xs'>
							{contextMenuState.neighborhoodName}
						</h5>
						<small className='w-full truncate text-neutral-600 text-xs'>
							{currentSubgeo
								? t('subgeo:ngbhds_part_of_subgeo', {
										name: currentSubgeo.name
									})
								: t('subgeo:unassigned')}
						</small>
					</div>
				</ContextMenuItem>

				<ContextMenuSeparator />

				<div className='flex min-h-0 w-full flex-1 flex-col p-2.5'>
					<NeighborhoodActions
						currentSubgeo={currentSubgeo}
						neighborhoodId={contextMenuState.neighborhoodId}
						onClose={onClose}
						showActionButtons={true}
						subgeos={subgeos}
					/>
				</div>
			</ContextMenuContent>
		</ContextMenu>
	)
}
